import type { ChangeEvent, FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, InputGroup, Spinner, TextInput } from '@george-labs.com/design-system';

import { useField, useWatchFieldValue } from '@luna/form2';
import { t } from '@luna/i18n';
import { useCountries } from '@shared/info-api';

import { CountrySelect } from './CountrySelect';
import type { PhoneInputNumber } from './FormPhoneInputService';
import {
    getCorrectNumberInfoAlert,
    getFormattedPhoneNumber,
    getInitialFormattedPhoneNumber,
    getInternationalPhoneNumber,
    getPhoneNumber,
    getPhoneSupportedCountryItems,
} from './FormPhoneInputService';
import { PhoneNumberAlert } from './PhoneNumberAlert';
import type { CountryCode, LibphoneApi } from './LiphoneApiTypes';
import type { FormFieldProps } from '../types/FormFieldProps';

interface Props extends FormFieldProps {
    libphoneApi: LibphoneApi;
    disabled?: boolean;
    info?: string;
    shouldClearOnUnmount?: boolean;
    autoFocus?: boolean;
}

export const FormPhoneInput: FC<Props> = ({
    libphoneApi: {
        getExampleNumber,
        isSupportedCountry,
        parsePhoneNumberFromString,
        getCountryCallingCode,
    },
    fieldName,
    disabled,
    autoFocus,
    info,
    'data-cy': dataCy,
    shouldClearOnUnmount,
}) => {
    const value = useWatchFieldValue<string | null | undefined, void>(fieldName);
    const [, { touched, error }, { setValue, setTouched }] = useField(fieldName, {
        shouldClearOnUnmount,
    });
    const { isPending: isLoadingCountries, data: countries } = useCountries();

    const [phoneNumber, setPhoneNumber] = useState<PhoneInputNumber>(
        getInitialFormattedPhoneNumber(parsePhoneNumberFromString, value),
    );
    const countryItems = useMemo(
        () => getPhoneSupportedCountryItems(isSupportedCountry, countries),
        [isSupportedCountry, countries],
    );

    const infos = [
        getCorrectNumberInfoAlert(getExampleNumber, touched, phoneNumber.countryCode, error),
        info,
    ];

    const formatPhoneNumber = () => {
        setPhoneNumber((prevPhoneNumber) =>
            getFormattedPhoneNumber(parsePhoneNumberFromString, prevPhoneNumber),
        );
    };

    const handlePhoneNumberChange = (nationalNumber: string, countryCode: CountryCode) => {
        const updatedPhoneNumber = getPhoneNumber(
            parsePhoneNumberFromString,
            nationalNumber,
            countryCode,
        );
        setPhoneNumber(updatedPhoneNumber);
        setValue(getInternationalPhoneNumber(parsePhoneNumberFromString, updatedPhoneNumber));
    };

    const handleCountryCodeChange = (countryCode: CountryCode) => {
        setTouched();
        handlePhoneNumberChange(phoneNumber.nationalNumber, countryCode);
        formatPhoneNumber();
    };

    const handleNationalNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        handlePhoneNumberChange(event.currentTarget.value, phoneNumber.countryCode);
    };

    const handleNationalNumberBlur = () => {
        formatPhoneNumber();
        setTouched();
    };

    useEffect(() => {
        const parsedInternationalPhoneNumber = getInternationalPhoneNumber(
            parsePhoneNumberFromString,
            phoneNumber,
        );
        if (parsedInternationalPhoneNumber === value) {
            return;
        }
        setValue(parsedInternationalPhoneNumber);
    }, []);

    if (isLoadingCountries) {
        return <Spinner center />;
    }

    return (
        <div data-cy={dataCy}>
            <InputGroup
                prepend={
                    <CountrySelect
                        value={phoneNumber.countryCode}
                        disabled={disabled}
                        items={countryItems}
                        getCountryCallingCode={getCountryCallingCode}
                        onChange={handleCountryCodeChange}
                    />
                }
            >
                <TextInput
                    aria-label={t('contactDataManagement.phoneContactForm.number')}
                    name="nationalNumber"
                    data-cy="national-number"
                    autoFocus={autoFocus}
                    disabled={disabled}
                    value={phoneNumber.nationalNumber}
                    onChange={handleNationalNumberChange}
                    onBlur={handleNationalNumberBlur}
                />
            </InputGroup>
            <div aria-live="polite">
                {touched && <PhoneNumberAlert variant={Alert.VARIANT.ERROR} alert={error} />}
                {infos.map(
                    (alert) =>
                        alert && (
                            <PhoneNumberAlert
                                key={alert}
                                variant={Alert.VARIANT.INFO}
                                alert={alert}
                            />
                        ),
                )}
            </div>
        </div>
    );
};
